///
/// EarthBrowser - SQL Terminal
///
/// REPL-style SQL terminal powered by SQLite with xterm.js
/// Created by Matt Giger on December 22, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { logger } from "@/utils/LogDispatcher"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { Terminal } from "xterm"
import { FitAddon } from "xterm-addon-fit"
import { WebLinksAddon } from "xterm-addon-web-links"
import "xterm/css/xterm.css"

export interface SQLTerminalHandle {
  focus: () => void
}

// LocalStorage keys for persistence
const STORAGE_KEY_HISTORY = "sql-terminal-history"
const MAX_HISTORY_ITEMS = 100

const isStatementComplete = (sql: string): boolean => {
  const trimmed = sql.trim()
  if (!trimmed) return true

  // Check if ends with semicolon
  if (trimmed.endsWith(';')) return true

  // Check if it's a dot command
  if (trimmed.startsWith('.')) return true

  // Check for unclosed strings
  let inString = false
  let stringChar = ''
  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed[i]
    if (!inString && (char === "'" || char === '"')) {
      inString = true
      stringChar = char
    } else if (inString && char === stringChar && trimmed[i - 1] !== '\\') {
      inString = false
    }
  }
  if (inString) return false

  // Check for unclosed parentheses
  let parenCount = 0
  for (const char of trimmed) {
    if (char === '(') parenCount++
    if (char === ')') parenCount--
  }
  if (parenCount > 0) return false

  // Otherwise incomplete
  return false
}

const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return "NULL"
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value)
  return JSON.stringify(value)
}

const formatResult = (result: unknown[]): string | undefined => {
  if (!result || result.length === 0) {
  //   return "Query executed successfully (0 rows)"
    return undefined
  }

  if (Array.isArray(result) && result.length > 0) {
    // Get column names from first row
    const firstRow = result[0] as Record<string, unknown>
    const columns = Object.keys(firstRow)

    // Format as table
    const rows = result.map((row) => {
      const r = row as Record<string, unknown>
      return columns.map((col) => formatCellValue(r[col])).join(" | ")
    })

    const header = columns.join(" | ")
    const separator = columns.map((col) => "-".repeat(col.length)).join("-+-")

    return [header, separator, ...rows, `\n(${result.length} rows)`].join("\n")
  }

  return JSON.stringify(result, null, 2)
}

export const SQLTerminal = forwardRef<SQLTerminalHandle>((_, ref) => {
  const [database, setDatabase] = useState<Database | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)

  const terminalRef = useRef<Terminal | null>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isMountedRef = useRef(true)
  const databaseRef = useRef<Database | null>(null)

  // SQL REPL state
  const bufferRef = useRef("")
  const cursorIndexRef = useRef(0)
  const promptRef = useRef("sqlite> ")
  const historyRef = useRef<string[]>([])
  const historyIndexRef = useRef<number | null>(null)

  // Multi-line SQL buffer
  const multiLineBufferRef = useRef<string[]>([])

  // Expose focus method via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      const terminal = terminalRef.current
      if (terminal) {
        terminal.focus()
      }
    }
  }))

  // Load persisted command history from localStorage
  const loadPersistedState = useCallback(() => {
    try {
      // Load command history
      const savedHistory = localStorage.getItem(STORAGE_KEY_HISTORY)
      if (savedHistory) {
        const parsed: unknown = JSON.parse(savedHistory)
        if (Array.isArray(parsed)) {
          const history = parsed.filter((item: unknown): item is string => typeof item === 'string')
          historyRef.current = history.slice(-MAX_HISTORY_ITEMS)
        }
      }
    } catch (error) {
      logger.warn("Failed to load SQL terminal persisted state:", error)
    }
  }, [])

  // Save command history to localStorage
  const saveHistory = useCallback(() => {
    try {
      const history = historyRef.current.slice(-MAX_HISTORY_ITEMS)
      localStorage.setItem(STORAGE_KEY_HISTORY, JSON.stringify(history))
    } catch (error) {
      logger.warn("Failed to save SQL terminal history:", error)
    }
  }, [])

  // Initialize database
  useEffect(() => {
    const db = Database.db
    setDatabase(db)
    databaseRef.current = db
  }, [])

  const refreshLine = useCallback(() => {
    const terminal = terminalRef.current
    if (!terminal) return

    const prompt = promptRef.current
    const buffer = bufferRef.current
    const cursorIndex = cursorIndexRef.current

    // Clear line and rewrite with cursor positioning
    const clearCommand = "\x1b[0K"
    const leftPart = prompt + buffer.slice(0, cursorIndex)
    const rightPart = buffer.slice(cursorIndex)
    terminal.write(`\x1b[0G${leftPart}\x1b[s${rightPart}${clearCommand}\x1b[u`)
  }, [])

  const setBuffer = useCallback((newBuffer: string, newCursorIndex: number | null = null) => {
    bufferRef.current = newBuffer
    if (newCursorIndex === null) {
      cursorIndexRef.current = newBuffer.length
    } else {
      cursorIndexRef.current = Math.max(0, Math.min(newCursorIndex, newBuffer.length))
    }
    refreshLine()
  }, [refreshLine])

  const addToHistory = useCallback((command: string) => {
    const trimmed = command.trimEnd()
    if (!trimmed) return
    const history = historyRef.current
    const last = history[history.length - 1]
    if (last !== trimmed) {
      historyRef.current = [...history, trimmed]
      saveHistory()
    }
  }, [saveHistory])

  const execSQL = useCallback(async (sql: string) => {
    const terminal = terminalRef.current
    const db = databaseRef.current
    if (!terminal || !db) return

    setIsExecuting(true)

    const trimmed = sql.trim()

    // Handle .clear command
    if (trimmed.toLowerCase() === ".clear" || trimmed.toLowerCase() === "clear") {
      terminal.clear()
      promptRef.current = "sqlite> "
      multiLineBufferRef.current = []
      setIsExecuting(false)
      return
    }

    // Handle .help command
    if (trimmed.toLowerCase() === ".help") {
      terminal.writeln("\x1b[36mSQLite Terminal Commands:\x1b[0m")
      terminal.writeln("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      terminal.writeln("\x1b[33mExecution:\x1b[0m")
      terminal.writeln("  Return             Execute current line")
      terminal.writeln("  ↑/↓                Navigate command history")
      terminal.writeln("  Ctrl+C             Interrupt/clear current line")
      terminal.writeln("")
      terminal.writeln("\x1b[33mSpecial Commands:\x1b[0m")
      terminal.writeln("  .clear             Clear terminal output")
      terminal.writeln("  .help              Show this help message")
      terminal.writeln("  .tables            List all tables in the database")
      terminal.writeln("  .indices           List all indices in the database")
      terminal.writeln("  .schema [TABLE]    Show CREATE statement for TABLE")
      terminal.writeln("")
      terminal.writeln("\x1b[33mExamples:\x1b[0m")
      terminal.writeln("  \x1b[36msqlite>\x1b[0m SELECT * FROM users LIMIT 10")
      terminal.writeln("  \x1b[36msqlite>\x1b[0m .tables")
      terminal.writeln("  \x1b[36msqlite>\x1b[0m .schema users")
      terminal.writeln("")
      terminal.writeln("\x1b[33mTips:\x1b[0m")
      terminal.writeln("  • Multi-line SQL: statements without ; continue on next line")
      terminal.writeln("  • URLs in output are clickable")
      terminal.writeln("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━")
      terminal.writeln("")
      setIsExecuting(false)
      return
    }

    // Handle .tables command
    if (trimmed.toLowerCase() === ".tables") {
      try {
        const result = await db.exec(
          "SELECT name FROM sqlite_master WHERE type='table' ORDER BY name",
          [],
          "high"
        )
        const formatted = formatResult(result as unknown[])
        const lines = formatted?.split("\n") ?? []
        lines.forEach((line) => {
          terminal.writeln(`\x1b[32m${line}\x1b[0m`)
        })
        terminal.writeln("")
      } catch (error) {
        terminal.writeln(
          `\x1b[31mError: ${error instanceof Error ? error.message : String(error)}\x1b[0m`
        )
        terminal.writeln("")
        logger.error("SQL execution error:", error)
      }
      setIsExecuting(false)
      return
    }

    if (trimmed.toLowerCase().startsWith(".indices")) {
      try {
        const result = await db.exec("SELECT name FROM sqlite_master WHERE type='index' ORDER BY name", [], "high")
        const formatted = formatResult(result as unknown[])
        const lines = formatted?.split("\n") ?? []
        lines.forEach((line) => {
          terminal.writeln(`\x1b[32m${line}\x1b[0m`)
        })
        terminal.writeln("")
      } catch (error) {
        terminal.writeln(
          `\x1b[31mError: ${error instanceof Error ? error.message : String(error)}\x1b[0m`
        )
        terminal.writeln("")
        logger.error("SQL execution error:", error)
      }
      setIsExecuting(false)
      return
    }

    // Handle .schema command
    if (trimmed.toLowerCase().startsWith(".schema")) {
      try {
        const parts = trimmed.split(/\s+/)
        const tableName = parts[1]
        let query: string
        if (tableName) {
          query = `SELECT sql FROM sqlite_master WHERE type='table' AND name='${tableName}'`
        } else {
          query = "SELECT sql FROM sqlite_master WHERE type='table' AND sql IS NOT NULL ORDER BY name"
        }
        const result = await db.exec(query, [], "high")
        const formatted = formatResult(result as unknown[])
        const lines = formatted?.split("\n") ?? []
        lines.forEach((line) => {
          terminal.writeln(`\x1b[32m${line}\x1b[0m`)
        })
        terminal.writeln("")
      } catch (error) {
        terminal.writeln(
          `\x1b[31mError: ${error instanceof Error ? error.message : String(error)}\x1b[0m`
        )
        terminal.writeln("")
        logger.error("SQL execution error:", error)
      }
      setIsExecuting(false)
      return
    }

    // Execute regular SQL
    try {
    //   const startTime = performance.now()
      const result = await db.exec(trimmed, [], "high")
    //   const executionTime = performance.now() - startTime

      const formatted = formatResult(result as unknown[])
      const lines = formatted?.split("\n") ?? []
      lines.forEach((line) => {
        terminal.writeln(`\x1b[32m${line}\x1b[0m`)
      })
    //   terminal.writeln(`\x1b[90m# ${executionTime.toFixed(0)}ms\x1b[0m`)
    //   terminal.writeln("")
    } catch (error) {
      terminal.writeln(
        `\x1b[31mError: ${error instanceof Error ? error.message : String(error)}\x1b[0m`
      )
      terminal.writeln("")
      logger.error("SQL execution error:", error)
    }

    setIsExecuting(false)
  }, [])

  const handleMultiLinePaste = useCallback(async (clipboard: string) => {
    const terminal = terminalRef.current
    if (!terminal) return

    const buffer = bufferRef.current
    const cursorIndex = cursorIndexRef.current

    // Check if clipboard contains newlines (multi-line paste)
    if (clipboard.includes("\n") || clipboard.includes("\r")) {
      // Handle multi-line paste - execute each line separately
      const lines = clipboard.split(/\r?\n/)

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].replace(/\u00a0/g, " ")

        // For first line, append to current buffer
        const fullLine = (i === 0)
          ? buffer.slice(0, cursorIndex) + line + buffer.slice(cursorIndex)
          : line

        // Skip the last empty line if we're at top-level prompt
        if (i === lines.length - 1 && line === "" && promptRef.current === "sqlite> ") {
          continue
        }

        // Write the line to terminal
        terminal.write(fullLine + "\r\n")

        // Add to multi-line buffer
        multiLineBufferRef.current.push(fullLine)
        const currentSQL = multiLineBufferRef.current.join('\n')

        if (isStatementComplete(currentSQL)) {
          // Execute complete statement
          await execSQL(currentSQL)
          multiLineBufferRef.current = []
          promptRef.current = "sqlite> "
          addToHistory(currentSQL)
        } else {
          // Continue multi-line
          promptRef.current = "   ...> "
        }

        // Write the prompt for the next line (unless it's the last line)
        if (i < lines.length - 1 || line !== "") {
          terminal.write(promptRef.current)
        }
      }

      // Reset buffer for next input
      bufferRef.current = ""
      cursorIndexRef.current = 0
    } else {
      // Single-line paste - just insert into buffer
      const newBuf = buffer.slice(0, cursorIndex) + clipboard + buffer.slice(cursorIndex)
      setBuffer(newBuf, cursorIndex + clipboard.length)
    }
  }, [addToHistory, execSQL, setBuffer])

  const setupTerminalDataHandler = useCallback(() => {
    const terminal = terminalRef.current
    if (!terminal) return

	// Check for the paste shortcut: Command+V on Mac (event.metaKey) or Ctrl+V on other platforms (event.ctrlKey)
	// Also allow Escape key to bubble up to window handlers for minimize functionality
	terminal.attachCustomKeyEventHandler(function (event) {
		if (event.type === 'keydown') {
		  // Allow Escape key to bubble up to window handlers
		  if (event.key === 'Escape') {
			// Return false to prevent xterm from consuming it
			// Don't call preventDefault() to allow it to bubble up
			return false
		  }

		  const isMacPaste = event.metaKey && event.key === 'v'
		  const isWinPaste = event.ctrlKey && event.key === 'v'

		  if (isMacPaste || isWinPaste) {
			// Prevent xterm.js from processing the key, allowing manual handling
			event.preventDefault()

			// Attempt to use the Clipboard API (modern approach)
			if (navigator.clipboard?.readText) {
			  navigator.clipboard.readText().then(text => {
				void handleMultiLinePaste(text)
			  }).catch(() => {
			  })
			}
			return false
		  }
		}
		return true
	})

    terminal.onData(async (data) => {
      const buffer = bufferRef.current
      const cursorIndex = cursorIndexRef.current
      const prompt = promptRef.current

      switch (data) {
        case "\r": // Enter
          terminal.write("\r\n")

          // Add to multi-line buffer
          multiLineBufferRef.current.push(buffer)
          const currentSQL = multiLineBufferRef.current.join('\n')

          if (isStatementComplete(currentSQL)) {
            // Execute complete statement
            await execSQL(currentSQL)
            multiLineBufferRef.current = []
            promptRef.current = "sqlite> "
            addToHistory(currentSQL)
          } else {
            // Continue multi-line
            promptRef.current = "   ...> "
          }

          bufferRef.current = ""
          cursorIndexRef.current = 0
          historyIndexRef.current = null
          terminal.write(promptRef.current)
          break

        case "\u0003": // Ctrl-C
          multiLineBufferRef.current = []
          bufferRef.current = ""
          cursorIndexRef.current = 0
          terminal.write("^C\r\nsqlite> ")
          promptRef.current = "sqlite> "
          historyIndexRef.current = null
          break

        case "\u0016": // Ctrl-V (paste) - fallback for browsers that don't support native paste
          try {
            const clipboard = await navigator.clipboard.readText()
            await handleMultiLinePaste(clipboard)
          } catch {
            // Clipboard access denied
          }
          break

        case "\u0001": // Ctrl-A: Move cursor to beginning of line
          if (cursorIndex > 0) {
            cursorIndexRef.current = 0
            refreshLine()
          }
          break

        case "\u0005": // Ctrl-E: Move cursor to end of line
          if (cursorIndex < buffer.length) {
            cursorIndexRef.current = buffer.length
            refreshLine()
          }
          break

        case "\u0015": // Ctrl-U: Clear line from cursor to beginning
          if (cursorIndex > 0) {
            const after = buffer.slice(cursorIndex)
            setBuffer(after, 0)
          }
          break

        case "\u0017": // Ctrl-W: Delete word before cursor
          if (cursorIndex > 0) {
            // Find the start of the word before cursor
            let wordStart = cursorIndex - 1
            // Skip trailing spaces
            while (wordStart > 0 && buffer[wordStart] === ' ') {
              wordStart--
            }
            // Find word boundary
            while (wordStart > 0 && buffer[wordStart - 1] !== ' ') {
              wordStart--
            }
            const before = buffer.slice(0, wordStart)
            const after = buffer.slice(cursorIndex)
            setBuffer(before + after, wordStart)
          }
          break

        case "\u007F": // Backspace
          if (cursorIndex > 0) {
            const before = buffer.slice(0, cursorIndex - 1)
            const after = buffer.slice(cursorIndex)
            setBuffer(before + after, cursorIndex - 1)
          }
          break

        case "\x1B[A": // Up arrow
          if (prompt === "sqlite> ") {
            const history = historyRef.current
            let historyIndex = historyIndexRef.current
            if (historyIndex === null) historyIndex = history.length
            if (historyIndex > 0) {
              historyIndex -= 1
              historyIndexRef.current = historyIndex
              const newBuf = history[historyIndex] || ""
              setBuffer(newBuf, newBuf.length)
            }
          }
          break

        case "\x1B[B": // Down arrow
          if (prompt === "sqlite> " && historyIndexRef.current !== null) {
            const history = historyRef.current
            let historyIndex = historyIndexRef.current
            if (historyIndex < history.length - 1) {
              historyIndex += 1
              historyIndexRef.current = historyIndex
              const newBuf = history[historyIndex] || ""
              setBuffer(newBuf, newBuf.length)
            } else {
              historyIndexRef.current = null
              setBuffer("", 0)
            }
          }
          break

        case "\x1B[C": // Right arrow
          if (cursorIndex < buffer.length) {
            cursorIndexRef.current += 1
            refreshLine()
          }
          break

        case "\x1B[D": // Left arrow
          if (cursorIndex > 0) {
            cursorIndexRef.current -= 1
            refreshLine()
          }
          break

        default:
          if (data) {
            // Insert at cursor
            const before = buffer.slice(0, cursorIndex)
            const after = buffer.slice(cursorIndex)
            const newBuf = before + data + after
            const newCursor = cursorIndex + data.length
            setBuffer(newBuf, newCursor)
          }
      }
    })
  }, [addToHistory, execSQL, handleMultiLinePaste, refreshLine, setBuffer])

  // Initialize terminal
  useEffect(() => {
    if (!containerRef.current) return

    // Load persisted command history
    loadPersistedState()

    // Create terminal
    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 12,
      fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace",
      allowTransparency: true,
      convertEol: true,
      theme: {
        background: "#00000000",
        foreground: "#e0e0e0",
        cursor: "#00ff00",
        cursorAccent: "#000000",
        selectionBackground: "#4d4d4d",
        black: "#000000",
        red: "#ff5555",
        green: "#50fa7b",
        yellow: "#f1fa8c",
        blue: "#bd93f9",
        magenta: "#ff79c6",
        cyan: "#8be9fd",
        white: "#e0e0e0",
        brightBlack: "#555555",
        brightRed: "#ff6e67",
        brightGreen: "#5af78e",
        brightYellow: "#f4f99d",
        brightBlue: "#caa9fa",
        brightMagenta: "#ff92d0",
        brightCyan: "#9aedfe",
        brightWhite: "#ffffff",
      },
      rows: 30,
      scrollback: 10000,
      allowProposedApi: true,
    })

    // Load addons
    const fitAddon = new FitAddon()
    const webLinksAddon = new WebLinksAddon()

    terminal.loadAddon(fitAddon)
    terminal.loadAddon(webLinksAddon)

    // Open terminal in container
    terminal.open(containerRef.current)

    const safeFit = () => {
      const el = containerRef.current
      if (!el || el.clientWidth < 1 || el.clientHeight < 1) return
      try {
        fitAddon.fit()
      } catch {
        // Container detached, terminal disposing, or xterm not ready
      }
    }

    safeFit()

    terminalRef.current = terminal
    fitAddonRef.current = fitAddon

    // Show welcome message
    terminal.writeln("\x1b[36m# SQLite Terminal\x1b[0m")
    terminal.writeln("\x1b[36m# Type .help for commands, Ctrl+C to interrupt\x1b[0m")
    terminal.writeln("")

    terminal.write(promptRef.current)

    // Set up data handler
    setupTerminalDataHandler()

    let terminalDisposed = false

    // Handle resize (skip 0×0 e.g. minimize/dock via Escape — fit() can crash a tearing-down xterm)
    const resizeObserver = new ResizeObserver(() => {
      if (terminalDisposed) return
      safeFit()
    })
    resizeObserver.observe(containerRef.current)

    return () => {
      terminalDisposed = true
      isMountedRef.current = false
      resizeObserver.disconnect()
      terminalRef.current = null
      fitAddonRef.current = null
      try {
        terminal.dispose()
      } catch {
        // Terminal already disposed
      }
    }
  }, [loadPersistedState, setupTerminalDataHandler])

  return (
    <div className="relative flex flex-col h-full rounded-lg overflow-hidden p-1">
      {/* xterm.js terminal container */}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-hidden" />

      {/* Status bar */}
      <div className="flex-shrink-0 border-t border-slate-700 px-4 py-1.5 text-[9px] text-muted-foreground/60 flex items-center justify-between">
        <div>
          {isExecuting
            ? "Executing..."
            : "Return: execute • ↑/↓: history • Ctrl+C: interrupt • Ctrl+F: search"}
        </div>
        {database && (
          <div className="text-green-400/60">
            SQLite Ready
          </div>
        )}
      </div>
    </div>
  )
})
